import { theme } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroupsByAssetGroupID } from 'reduxStore/assetGroups/selectors';
import { Loader } from 'zComponents/atoms';
import { ManageDevicesGroupsDropdown } from 'zComponents/molecules';
import { Col, Divider, Drawer, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import {
	DevicesPanelContent,
	DevicesPanelExtra,
	EditDrawerHeader,
	EditSiteSelect,
	PanelHeader,
	PermissionPanelContent,
	PermissionPanelExtra
} from './components';
import StatusIcon from 'assets/images/svg/StatusIcon';
import { DevicesIcon, EnableIcon, NumberIcon } from 'assets/images/svg';
import { SitesIcon } from 'assets/images/svg/Menu';
import Collapse from 'zui/Antd/Collapse';

const DeviceGroupsDrawer = ({ groupID, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const {
		token: { borderRadiusLG, paddingXXL = 48, marginXS, paddingLG, padding, paddingMD }
	} = theme.useToken();
	const devicesRef = useRef(null);
	const permissionRef = useRef(null);

	// ✅ Redux State
	const group = useSelector((state) => selectAssetGroupsByAssetGroupID(state, groupID));

	// ✅ Local State
	const [activeKeys, setActiveKeys] = useState(['Devices', 'Permissions']);
	const [isEditingDevices, setIsEditingDevices] = useState(false);
	const [isEditingPermissions, setIsEditingPermissions] = useState(false);
	const [isDevicesModalOpen, setIsDevicesModalOpen] = useState(false);
	const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	const visible = visibleProp ? visibleProp : internalVisible;
	const setVisible = setVisibleProp ? setVisibleProp : setInternalVisible;

	// ✅ Close drawer and reset everything
	const handleOnClose = () => {
		setVisible(false);
	};

	const togglePanel = (key) => {
		setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	};

	const handleCollapseChange = (keys) => {
		if (isDevicesModalOpen || isPermissionModalOpen) {
			return;
		}
		setActiveKeys(keys);

		// If the user closes the panel, we should reset the editing state
		if (!keys.includes('Devices') && isEditingDevices) {
			setIsEditingDevices(false);
		}
		if (!keys.includes('Permissions') && isEditingPermissions) {
			setIsEditingPermissions(false);
		}
	};

	const items = [
		{
			key: 'Devices',
			label: (
				<PanelHeader
					title='Devices'
					iconLeft={<DevicesIcon />}
					isExpanded={activeKeys.includes('Devices')}
					togglePanel={() => togglePanel('Devices')}
				/>
			),
			children: (
				<div ref={devicesRef}>
					<DevicesPanelContent
						groupID={groupID}
						isEditing={isEditingDevices}
						setIsEditing={setIsEditingDevices}
					/>
				</div>
			),
			extra: (
				<DevicesPanelExtra
					groupID={groupID}
					isEditing={isEditingDevices}
					setIsEditing={setIsEditingDevices}
					activeKeys={activeKeys}
					setActiveKeys={setActiveKeys}
					wrapperRef={devicesRef}
					onModalOpenChange={setIsDevicesModalOpen}
				/>
			),
			styles: {
				header: {
					marginTop: marginXS
				}
			}
		},
		{
			key: 'divider-1',
			label: <Divider style={{ margin: 0 }} />,
			children: null,
			collapsible: 'disabled',
			showArrow: false,
			style: { padding: 0 }
		},
		{
			key: 'Permissions',
			label: (
				<PanelHeader
					title='Access Permissions'
					iconLeft={<EnableIcon width={16} height={16} />}
					isExpanded={activeKeys.includes('Permissions')}
					togglePanel={() => togglePanel('Permissions')}
				/>
			),
			children: (
				<div ref={permissionRef}>
					<PermissionPanelContent
						groupID={groupID}
						isEditing={isEditingPermissions}
						setIsEditing={setIsEditingPermissions}
					/>
				</div>
			),
			extra: (
				<PermissionPanelExtra
					groupID={groupID}
					isEditing={isEditingPermissions}
					setIsEditing={setIsEditingPermissions}
					activeKeys={activeKeys}
					setActiveKeys={setActiveKeys}
					wrapperRef={permissionRef}
					onModalOpenChange={setIsPermissionModalOpen}
				/>
			),
			styles: {
				header: {
					marginTop: marginXS
				}
			}
		}
	];

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			styles={drawerStyles}
			destroyOnClose={true}
		>
			{
				// ✅ Loading Spinner
				!group ? (
					<Loader />
				) : (
					<Flex vertical>
						<EditDrawerHeader
							groupID={group?.groupID}
							name={group?.name}
							description={group?.description}
							handleOnCloseEdit={handleOnClose}
						/>
						<div
							style={{
								marginTop: paddingXXL
							}}
						>
							<ManageDevicesGroupsDropdown groupIDs={[groupID]} />
						</div>

						<Divider style={{ margin: `${paddingMD}px 0px` }} />

						<Row>
							<Col span={24}>
								<Flex vertical gap={paddingLG} style={{ padding: padding }}>
									<InfoTuple
										icon={<SitesIcon />}
										text='Site'
										value={
											<EditSiteSelect
												siteID={group?.siteID}
												groupID={groupID}
											/>
										}
									/>
									<InfoTuple
										icon={<NumberIcon />}
										text='Devices'
										value={
											<>
												{group?.devices?.length}{' '}
												{group?.devices?.length === 1 ? 'Device' : 'Devices'}
											</>
										}
									/>
									<InfoTuple icon={<StatusIcon />} text='Status' value='-' />
								</Flex>
							</Col>
						</Row>

						<Divider style={{ margin: `${paddingMD}px 0px` }} />

						<Collapse
							expandIcon={() => null}
							items={items}
							activeKey={activeKeys}
							onChange={handleCollapseChange}
							ghost
						/>
					</Flex>
				)
			}
		</Drawer>
	);
};

export default DeviceGroupsDrawer;
